import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
// Lazily loaded: the planner shares nothing with the terminal, and a static
// import would ship the whole engine, its UI and its database layer to every
// chart user who never opens it.
const PlannerPage = React.lazy(() => import('./prop-hedge/components/PlannerPage'));
// Same reasoning: the trades page reads completed round turns from the engine
// and shares nothing with the chart, so it must not be bundled into it.
const TradesPage = React.lazy(() => import('./trades/TradesPage'));
// The Vest scanner is read-only market data with its own socket; it must not
// boot the trading app or ship inside the chart bundle either.
const ScannerPage = React.lazy(() => import('./scanner/ScannerPage'));
// Our GEX levels against free vendors: a read-only report page, same reasoning.
const GexComparePage = React.lazy(() => import('./gexcompare/GexComparePage'));
import './index.css';

// Listen for service worker updates - only reload if page has been active for a while
// This prevents reload loops during initial page load
if ('serviceWorker' in navigator) {
  const pageLoadTime = Date.now();
  const MIN_UPTIME_FOR_RELOAD = 30000; // Avoid disruptive reloads during initial hydration
  let swUpdatePending = false;

  const maybeReloadForSwUpdate = () => {
    const uptime = Date.now() - pageLoadTime;
    if (!swUpdatePending) return;

    if (document.visibilityState === 'visible') {
      console.log('[App] Update available, deferring reload while page is active');
      return;
    }

    if (uptime > MIN_UPTIME_FOR_RELOAD) {
      console.log('[App] Applying deferred service worker update');
      swUpdatePending = false;
      setTimeout(() => {
        window.location.reload();
      }, 100);
    }
  };

  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data?.type === 'SW_UPDATED') {
      swUpdatePending = true;
      console.log('[App] SW_UPDATED received', {
        uptimeMs: Date.now() - pageLoadTime,
        visibilityState: document.visibilityState,
      });
      maybeReloadForSwUpdate();
    }
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      maybeReloadForSwUpdate();
    }
  });

  // Also check for updates periodically (every 5 minutes)
  setInterval(() => {
    console.log('[App] Polling service worker registration for updates');
    navigator.serviceWorker.getRegistration().then((reg) => {
      if (reg) {
        reg.update().catch(() => {
          // Ignore update errors (offline, etc.)
        });
      }
    });
  }, 5 * 60 * 1000);
}

/**
 * Minimal routing: the planner, the trades page, the Vest scanner and the GEX
 * vendor comparison are their own pages, not parts of the terminal.
 *
 * A path check rather than a router because these are the only routes, and
 * because none of them may boot the trading app — no passcode gate, no
 * websocket, no chart, no broker polling. They share nothing with the terminal
 * beyond the stylesheet.
 */
const path = window.location.pathname.replace(/\/+$/, '');
const standalone =
  path === '/planner'
    ? { label: 'planner', element: <PlannerPage /> }
    : path === '/trades'
      ? { label: 'trades', element: <TradesPage /> }
      : path === '/scanner'
        ? { label: 'scanner', element: <ScannerPage /> }
        : path === '/gex-compare'
          ? { label: 'GEX comparison', element: <GexComparePage /> }
          : null;

ReactDOM.createRoot(document.getElementById('root')!).render(
  standalone ? (
    <React.Suspense
      fallback={<div style={{ padding: 24, color: '#8899ab' }}>Loading {standalone.label}…</div>}
    >
      {standalone.element}
    </React.Suspense>
  ) : (
    <App />
  )
);
